import { FieldArray, getIn, useFormikContext } from "formik";
import { IbanInput } from "components/forms/IbanInput";
import { Landmark, Plus, Trash2 } from "lucide-react";
import { BankAccountFormRow, EMPTY_BANK_ACCOUNT_ROW } from "./bankAccountTypes";

interface BankAccountsFieldArrayProps {
  /** Nom du champ Formik contenant le tableau de comptes (BankAccountFormRow[]). */
  name: string;
  /** Texte affiché quand la liste est vide. */
  emptyLabel?: string;
  /** Note d'aide affichée sous la liste (ex. RIB). */
  helpText?: string;
}

const inputClasses =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

/**
 * Liste dynamique de comptes bancaires (IBAN / BIC / libellé / compte par défaut),
 * form-agnostique : réutilisable dans n'importe quel formulaire Formik via `name`.
 * Les comptes deviennent des CompteBancaire rattachés à la Personne côté serveur.
 */
export const BankAccountsFieldArray: React.FC<BankAccountsFieldArrayProps> = ({
  name,
  emptyLabel = "Aucun compte pour l'instant.",
  helpText,
}) => {
  const { values, errors, touched, submitCount, setFieldValue, handleChange, handleBlur } =
    useFormikContext<Record<string, unknown>>();

  const rows: BankAccountFormRow[] = (getIn(values, name) as BankAccountFormRow[]) ?? [];

  const fieldError = (index: number, field: keyof BankAccountFormRow): string | undefined => {
    const path = `${name}.${index}.${field}`;
    const isTouched = submitCount > 0 || getIn(touched, path);
    const error = getIn(errors, path);
    return isTouched && typeof error === 'string' ? error : undefined;
  };

  // Un seul compte par défaut : cocher une ligne décoche les autres.
  const setDefault = (index: number, checked: boolean) => {
    rows.forEach((_, i) => {
      setFieldValue(`${name}.${i}.isDefault`, checked && i === index);
    });
  };

  return (
    <FieldArray name={name}>
      {({ push, remove }) => (
        <div className="flex flex-col gap-3">
          {rows.length === 0 && <p className="text-sm text-gray-500">{emptyLabel}</p>}

          {rows.map((row, index) => (
            <div key={index} className="rounded-lg border border-gray-200 bg-white p-3">
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-4">
                  <IbanInput
                    className={`${inputClasses} !h-auto`}
                    value={row.iban}
                    onChange={(v) => setFieldValue(`${name}.${index}.iban`, v)}
                    onBlur={handleBlur}
                    name={`${name}.${index}.iban`}
                    placeholder="IBAN (FR76 ...)"
                  />
                  {fieldError(index, 'iban') && (
                    <p className="mt-1 text-xs text-red-600">{fieldError(index, 'iban')}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <input
                    name={`${name}.${index}.bic`}
                    value={row.bic}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="BIC (optionnel)"
                    className={inputClasses}
                  />
                </div>
                <div className="col-span-4">
                  <input
                    name={`${name}.${index}.label`}
                    value={row.label}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Libellé (ex. Compte commissions)"
                    className={inputClasses}
                  />
                  {fieldError(index, 'label') && (
                    <p className="mt-1 text-xs text-red-600">{fieldError(index, 'label')}</p>
                  )}
                </div>
                <div className="col-span-2 flex items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={row.isDefault}
                      onChange={(e) => setDefault(index, e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    Par défaut
                  </label>
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                    aria-label="Supprimer ce compte"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() => push({ ...EMPTY_BANK_ACCOUNT_ROW })}
            className="inline-flex items-center gap-2 self-start rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-primary hover:text-primary transition-colors"
          >
            <Plus size={16} />
            Ajouter un compte bancaire
          </button>

          {helpText && (
            <p className="flex items-start gap-1.5 text-xs text-gray-500">
              <Landmark size={14} className="mt-0.5 flex-shrink-0 text-gray-400" />
              {helpText}
            </p>
          )}
        </div>
      )}
    </FieldArray>
  );
};
